import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import getExternalStorageDisconnectPreview from '@/settings/fetch/get-external-storage-disconnect-preview'
import type { ConnectedAccount, ExternalStorageDisconnectFileDisposition, ExternalStorageDisconnectPreview } from '@/settings/schemas/external-storage-schema'

export type DisconnectExternalStorageAccountSelection = {
  fileDisposition: ExternalStorageDisconnectFileDisposition
}

type DisconnectExternalStorageAccountDialogProps = {
  account: ConnectedAccount
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (selection: DisconnectExternalStorageAccountSelection) => Promise<boolean>
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`
}

type DispositionCardProps = {
  description: string
  disabled: boolean
  disposition: ExternalStorageDisconnectFileDisposition
  label: string
  recommended?: boolean
  selected: boolean
  onSelect: (disposition: ExternalStorageDisconnectFileDisposition) => void
}

function DispositionCard({ description, disabled, disposition, label, recommended = false, selected, onSelect }: DispositionCardProps) {
  const selectedStyles = disposition === 'keep' ? 'border-brand-200 bg-brand-50' : 'border-red-300 bg-red-50'
  const selectedRadioStyles = disposition === 'keep' ? 'border-brand-500' : 'border-brand-neutral-950'

  return (
    <label
      className={clsx(
        'flex flex-col gap-2 rounded-lg border p-4',
        selected ? selectedStyles : 'border-border-default bg-surface-dialog',
        disabled ? 'cursor-default' : 'cursor-pointer'
      )}
    >
      <span className="flex items-start gap-2">
        <span className="flex min-h-6 min-w-0 flex-1 items-start gap-2">
          <input
            className="peer sr-only"
            type="radio"
            name="external-storage-file-disposition"
            value={disposition}
            aria-label={label}
            checked={selected}
            disabled={disabled}
            onChange={() => onSelect(disposition)}
          />
          <span
            aria-hidden
            className={clsx(
              'mt-0.5 size-5 shrink-0 rounded-full bg-white peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
              selected ? `border-[5px] ${selectedRadioStyles}` : 'border border-border-strong'
            )}
          />
          <span className="font-rethink-sans text-base font-medium leading-6 text-text-primary">{label}</span>
        </span>
        {recommended && <span className="shrink-0 rounded bg-brand-neutral-950 px-1 py-px text-[10px] font-medium leading-[11px] text-white">RECOMMENDED</span>}
      </span>
      <span className="pl-7 text-sm leading-5 tracking-[-0.15px] text-text-secondary">{description}</span>
    </label>
  )
}

export default function DisconnectExternalStorageAccountDialog({ account, open, onOpenChange, onConfirm }: DisconnectExternalStorageAccountDialogProps) {
  const providerLabel = kExternalStorageProviderDisplayName[account.provider]
  const [disposition, setDisposition] = useState<ExternalStorageDisconnectFileDisposition>('keep')
  const [preview, setPreview] = useState<ExternalStorageDisconnectPreview | null>(null)
  const [previewError, setPreviewError] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    if (!open) return

    setDisposition('keep')
    setSubmitError(false)
    setIsSubmitting(false)
  }, [account.id, open])

  useEffect(() => {
    if (!open) return

    let active = true
    setPreview(null)
    setPreviewError(false)
    setIsLoadingPreview(true)

    void getExternalStorageDisconnectPreview(account.id)
      .then((nextPreview) => {
        if (!active) return
        setPreview(nextPreview)
      })
      .catch(() => {
        if (!active) return
        setPreviewError(true)
      })
      .finally(() => {
        if (!active) return
        setIsLoadingPreview(false)
      })

    return () => {
      active = false
    }
  }, [account.id, loadAttempt, open])

  const handleConfirm = async () => {
    if (isSubmitting || (disposition === 'remove' && !preview)) return

    setIsSubmitting(true)
    setSubmitError(false)
    try {
      const disconnected = await onConfirm({ fileDisposition: disposition })
      if (disconnected) {
        onOpenChange(false)
        return
      }
      setSubmitError(true)
    } catch {
      setSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const fileLabel = preview ? countLabel(preview.file_count, 'file', 'files') : null
  const matterLabel = preview ? countLabel(preview.matter_count, 'matter', 'matters') : null

  return (
    <Modal open={open} onOpenChange={(nextOpen) => !isSubmitting && onOpenChange(nextOpen)}>
      <ModalContent
        title={`Disconnect ${providerLabel}?`}
        subtitle={
          fileLabel
            ? `This disconnects your ${providerLabel} account and stops syncing folders for all matters. Choose what happens to the ${fileLabel} already in Paxton.`
            : `This disconnects your ${providerLabel} account and stops syncing folders for all matters.`
        }
        width="md"
        height="max-md"
        className="max-w-[568px]"
        preventDismiss={isSubmitting}
        // Opened from within the SettingsModal, so stack above its overlay/content (repo's second-dialog pattern).
        stacked
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            inlineMessage={submitError ? { variant: 'error', children: `${providerLabel} failed to disconnect. Please try again.` } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: () => onOpenChange(false), disabled: isSubmitting }}
            primaryAction={{
              label: 'Disconnect',
              onClick: handleConfirm,
              loading: isSubmitting,
              disabled: isSubmitting,
            }}
          />
        }
      >
        <fieldset className="flex flex-col gap-4" disabled={isSubmitting}>
          <legend className="sr-only">Choose what happens to files already in Paxton</legend>
          <DispositionCard
            disposition="keep"
            label="Keep files in Paxton"
            description={`Files stay in your matters and remain searchable. They stop updating from ${providerLabel}.`}
            selected={disposition === 'keep'}
            disabled={isSubmitting}
            recommended
            onSelect={setDisposition}
          />
          {isLoadingPreview ? (
            <div className="flex items-center justify-center gap-3 py-4 text-sm text-brand-neutral-700" aria-busy="true">
              <CircularProgressContinuousSized size={20} thickness={6} />
              Loading synced file counts…
            </div>
          ) : previewError ? (
            <InlineMessage variant="error" className="w-full items-center">
              <span className="flex flex-wrap items-center justify-between gap-2">
                Unable to load synced file counts.
                <Button color="destructive" size="xs" variant="plain" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                  Retry
                </Button>
              </span>
            </InlineMessage>
          ) : preview && fileLabel && matterLabel ? (
            <DispositionCard
              disposition="remove"
              label="Remove files from Paxton"
              description={`${fileLabel} across ${matterLabel} ${
                preview.file_count === 1 ? 'is' : 'are'
              } removed from Paxton. Any references to removed files will be broken. This can’t be undone.`}
              selected={disposition === 'remove'}
              disabled={isSubmitting}
              onSelect={setDisposition}
            />
          ) : null}
        </fieldset>
      </ModalContent>
    </Modal>
  )
}
